/**
 * Client-side auction-flow request builders — the boundary between the auction
 * UI (`auction-panel` bid bar + `auction-winner-checkout-sheet` settle) and
 * the HTTP routes under `app/api/auctions/...`.
 *
 *   components/detailed-listing-view ── intent ──▶ builders here
 *                                                       │
 *                                                       ├── safeParse (boundary)
 *                                                       └── fetch + JSON parse
 *
 * IMPORTANT — do NOT confuse with `lib/auction-bids.ts` or `lib/auction-settle.ts`.
 * Those files are the SERVER-side domain: they accept a `SupabaseClient`, talk
 * to `supabaseAdmin`, fire `broadcastBid` / `notifyOutbid` side effects, and
 * are imported only by the `app/api/auctions/...` route handlers. This file
 * is the CLIENT-side shell: it accepts intent-style args, runs the
 * `@kraahl/contract` Zod safeParse for body-carrying endpoints, then `fetch`es.
 * Same false-positive shape as offers had pre-`refactor/offers-hex-extract`
 * (`lib/offers/offer-actions.ts` vs `lib/offers/offer-builders.ts`).
 *
 * Why this exists:
 *  - Inline `fetch('/api/auctions/...')` calls were grandfathered inside
 *    `components/detailed-listing-view/auction-panel.tsx` (G17 — place-bid) and
 *    `components/detailed-listing-view/auction-winner-checkout-sheet.tsx` (G19
 *    — auction-settle) — see `.tondocs/FRONTEND_WIRING_STRATEGY.md §3.2`.
 *    Sibling-D D2 added compile-time `satisfies <ContractType>` pins on the
 *    body literals; this extract is the next step — pull body assembly +
 *    boundary safeParse into a `lib/` builder so a Walking Skeleton test can
 *    drive producer + consumer in one call (no hand-coded body, no tautology).
 *  - Both auction endpoints are web-only today (mobile never hits
 *    `/api/auctions/place-bid` or `/api/auctions/[id]/settle`). The builders
 *    still read `@kraahl/contract` schemas — identical pattern to
 *    `lib/offers/offer-builders.ts` so a future mobile auction surface can
 *    reuse them directly.
 *  - Component tests can mock `fetch` (the network seam) and let the real
 *    builder run — required to NOT regress to Coverage Theatre, the failure
 *    mode of `paypal-checkout.test.tsx` pre-S4 (mocking the builder
 *    short-circuits safeParse).
 *
 * Design notes:
 *  - Builders accept INTENT-style args (camelCase, what the caller has from
 *    state). Body assembly (snake_case for place-bid, `orderId` for settle)
 *    is the builder's job — that's the entire point of the seam.
 *  - place-bid + settle each run `Schema.safeParse(body)` before fetch and
 *    throw `AuctionContractError` on rejection — same shape as
 *    `lib/offers/offer-builders.ts::OfferContractError` and
 *    `lib/payments/checkout-actions.ts::CheckoutContractError`. Programmer-bug
 *    signal: the route would 400 anyway, this surfaces it at the responsible
 *    line.
 *  - Response shape is NOT pinned by Zod here (same reasoning as offers /
 *    checkout-actions). Callers keep their `{ ok, status, data }` consumer
 *    surface — `auction-panel` reads `data.new_highest_bid` / `data.new_end_at`
 *    etc., `auction-winner-checkout-sheet` reads `data.error` to drive its
 *    409-idempotent retry path.
 *
 * @see .tondocs/FRONTEND_WIRING_STRATEGY.md §3.1 / §3.2 G17 / G19
 * @see .tondocs/skills/hex.md §2 — Functional Core / Imperative Shell
 * @see lib/auction-bids.ts — SERVER-side domain (do NOT confuse)
 * @see lib/auction-settle.ts — SERVER-side domain (do NOT confuse)
 * @see lib/offers/offer-builders.ts — sibling pattern for the offer cluster
 * @see lib/payments/checkout-actions.ts — sibling pattern for the cart cluster
 * @see packages/contract/src/auction-place-bid.ts — PlaceAuctionBidRequestSchema
 * @see packages/contract/src/auction-settle.ts — AuctionSettleRequestSchema
 */

import {
  AuctionSettleRequestSchema,
  PlaceAuctionBidRequestSchema,
  type AuctionSettleRequest,
  type PlaceAuctionBidRequest,
} from "@kraahl/contract"

// ---------------------------------------------------------------------------
// Shared error + result envelope
// ---------------------------------------------------------------------------

/**
 * Thrown when a request body fails the contract Zod parse before fetch.
 * Programmer-bug signal: the caller assembled an intent the contract does
 * not accept. The error message includes the route path so the failure is
 * greppable; details carry Zod's issue list for the responsible developer.
 * Mirrors `OfferContractError` from `lib/offers/offer-builders.ts`.
 */
export class AuctionContractError extends Error {
  readonly route: string
  readonly issues: ReadonlyArray<{ path: string; message: string }>
  constructor(
    route: string,
    issues: ReadonlyArray<{ path: string; message: string }>,
  ) {
    super(
      `[auction-builders] contract parse failed for ${route}: ${issues
        .map((i) => `${i.path}: ${i.message}`)
        .join("; ")}`,
    )
    this.name = "AuctionContractError"
    this.route = route
    this.issues = issues
  }
}

/**
 * Envelope returned by every builder. Response shape is intentionally opaque
 * (`unknown`) — response schemas live as a separate response-side pass, see
 * file header. Callers keep their `{ ok, status, data }` consumer surface.
 */
export interface AuctionBuilderResult {
  ok: boolean
  status: number
  data: unknown
}

async function readJson(res: Response): Promise<unknown> {
  return res.json().catch(() => null)
}

// ---------------------------------------------------------------------------
// G17 — placeAuctionBid
// ---------------------------------------------------------------------------

export interface PlaceAuctionBidIntent {
  listingId: string
  amount: number
  /**
   * Optional client-supplied idempotency token. Stored on the bid row so a
   * web-side retry of the same logical action coalesces server-side. The
   * caller (`auction-panel`) builds it as
   * `client-${listingId}-${Date.now()}-${eventVersion}` — kept opaque to the
   * builder since callers in other surfaces (future mobile) may shape it
   * differently. Schema only requires `min(1)` on the string when present.
   */
  clientBidId?: string
}

export async function placeAuctionBid(
  intent: PlaceAuctionBidIntent,
): Promise<AuctionBuilderResult> {
  const body = {
    listing_id: intent.listingId,
    amount: intent.amount,
    client_bid_id: intent.clientBidId,
  } satisfies PlaceAuctionBidRequest

  const parsed = PlaceAuctionBidRequestSchema.safeParse(body)
  if (!parsed.success) {
    throw new AuctionContractError(
      "/api/auctions/place-bid",
      parsed.error.issues.map((i) => ({
        path: i.path.join("."),
        message: i.message,
      })),
    )
  }

  const res = await fetch("/api/auctions/place-bid", {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  })
  return { ok: res.ok, status: res.status, data: await readJson(res) }
}

// ---------------------------------------------------------------------------
// G19 — settleAuction
// ---------------------------------------------------------------------------

export interface SettleAuctionIntent {
  /**
   * `listings.id` of the auction being settled — used only for URL routing
   * (`/api/auctions/[listingId]/settle`), not in the body. Mirrors the route
   * handler which reads it from `params`, NOT from `req.json()`.
   */
  listingId: string
  /**
   * `orders.id` of the row written during the winning capture. The only
   * field the route's safeParse looks at — see `AuctionSettleRequestSchema`.
   */
  orderId: string
}

export async function settleAuction(
  intent: SettleAuctionIntent,
): Promise<AuctionBuilderResult> {
  const body = { orderId: intent.orderId } satisfies AuctionSettleRequest
  const route = `/api/auctions/${encodeURIComponent(intent.listingId)}/settle`

  const parsed = AuctionSettleRequestSchema.safeParse(body)
  if (!parsed.success) {
    throw new AuctionContractError(
      route,
      parsed.error.issues.map((i) => ({
        path: i.path.join("."),
        message: i.message,
      })),
    )
  }

  const res = await fetch(route, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  })
  return { ok: res.ok, status: res.status, data: await readJson(res) }
}
